import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import {
  BugReport,
  CheckCircle,
  ErrorOutline,
  FirstPage,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
  LastPage,
  PendingActions,
  Security,
  WarningAmber
} from '@mui/icons-material';

const levelMeta = {
  DEBUG: { color: 'default', icon: <BugReport fontSize="small" /> },
  INFO: { color: 'info', icon: <CheckCircle fontSize="small" /> },
  WARNING: { color: 'warning', icon: <WarningAmber fontSize="small" /> },
  ERROR: { color: 'error', icon: <ErrorOutline fontSize="small" /> },
  SECURITY: { color: 'success', icon: <Security fontSize="small" /> },
  CRITICAL: { color: 'error', icon: <ErrorOutline fontSize="small" /> }
};

const statusMeta = {
  success: { color: 'success', icon: <CheckCircle fontSize="small" /> },
  failed: { color: 'error', icon: <ErrorOutline fontSize="small" /> },
  pending: { color: 'warning', icon: <PendingActions fontSize="small" /> }
};

const numberFormat = (value) => Number(value || 0).toLocaleString();

const valueOrDash = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  return value;
};

const formatJson = (data) => {
  if (!data) {
    return 'No data';
  }

  if (typeof data === 'string') {
    return data;
  }

  return JSON.stringify(data, null, 2);
};

const LogsTable = ({
  logs,
  pagination,
  sortBy,
  sortDirection,
  perPageOptions,
  onPageChange,
  onRowsPerPageChange,
  onSortChange
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const currentPage = Number(pagination.current_page || 1);
  const lastPage = Number(pagination.last_page || 1);
  const perPage = Number(pagination.per_page || 10);

  const pageNumbers = useMemo(() => {
    const visible = 5;
    let start = Math.max(1, currentPage - Math.floor(visible / 2));
    let end = Math.min(lastPage, start + visible - 1);

    start = Math.max(1, end - visible + 1);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentPage, lastPage]);

  const SortHeader = ({ id, children, width }) => (
    <TableCell width={width} sortDirection={sortBy === id ? sortDirection : false}>
      <TableSortLabel
        active={sortBy === id}
        direction={sortBy === id ? sortDirection : 'asc'}
        onClick={() => onSortChange(id)}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  const renderDetail = (log) => (
    <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2, bgcolor: '#f8fafc' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Record
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">ID: {valueOrDash(log.record_id)}</Typography>
            <Typography variant="body2">Type: {valueOrDash(log.record_type)}</Typography>
            <Typography variant="body2">Request: {valueOrDash(log.request_id)}</Typography>
            <Typography variant="body2">IP: {valueOrDash(log.ip_address)}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Actor
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">Performed By: {valueOrDash(log.performed_by)}</Typography>
            <Typography variant="body2">User ID: {valueOrDash(log.user_id)}</Typography>
            <Typography variant="body2">Role: {valueOrDash(log.user_role)}</Typography>
            <Typography variant="body2">System Task: {valueOrDash(log.system_task)}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Error
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">Code: {valueOrDash(log.error_code)}</Typography>
            <Typography variant="body2">File: {valueOrDash(log.error_file)}</Typography>
            <Typography variant="body2">Line: {valueOrDash(log.error_line)}</Typography>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Message
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {valueOrDash(log.log_message)}
          </Typography>
        </Grid>

        {log.changed_fields && (
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Changed Fields
            </Typography>
            <Box component="pre" sx={preSx}>
              {formatJson(log.changed_fields)}
            </Box>
          </Grid>
        )}
        {log.old_data && (
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Old Data
            </Typography>
            <Box component="pre" sx={preSx}>
              {formatJson(log.old_data)}
            </Box>
          </Grid>
        )}
        {log.new_data && (
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              New Data
            </Typography>
            <Box component="pre" sx={preSx}>
              {formatJson(log.new_data)}
            </Box>
          </Grid>
        )}
        {log.user_agent && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              User Agent
            </Typography>
            <Box component="pre" sx={preSx}>
              {log.user_agent}
            </Box>
          </Grid>
        )}
        {log.stack_trace && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Stack Trace
            </Typography>
            <Box component="pre" sx={preSx}>
              {log.stack_trace}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  return (
    <>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', minHeight: 320 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width={52} />
              <SortHeader id="created_at" width={170}>Date & Time</SortHeader>
              <SortHeader id="log_level" width={120}>Level</SortHeader>
              <SortHeader id="classification" width={155}>Classification</SortHeader>
              <SortHeader id="module" width={170}>Module</SortHeader>
              <SortHeader id="action" width={130}>Action</SortHeader>
              <SortHeader id="status" width={110}>Status</SortHeader>
              <SortHeader id="performed_by" width={145}>Actor</SortHeader>
              <SortHeader id="record_id" width={150}>Record</SortHeader>
              <TableCell width={360}>Message</TableCell>
              <SortHeader id="ip_address" width={145}>IP Address</SortHeader>
              <SortHeader id="request_id" width={160}>Request ID</SortHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={12}>
                  <Box sx={{ py: 7, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={800}>
                      No logs found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Adjust the filters or date range.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {logs.map((log) => {
              const isExpanded = expandedRow === log.id;
              const level = levelMeta[log.log_level] || levelMeta.INFO;
              const status = statusMeta[log.status] || statusMeta.success;

              return (
                <React.Fragment key={log.id}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      '& > td': { borderBottom: isExpanded ? 0 : undefined }
                    }}
                    onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                  >
                    <TableCell>
                      <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                        <IconButton size="small">
                          {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {log.created_at_formatted || log.created_at}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{log.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={level.icon}
                        label={log.log_level}
                        color={level.color}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={log.classification} variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800} noWrap>
                        {log.module}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {valueOrDash(log.sub_module)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} color="primary" variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={status.icon}
                        label={log.status}
                        color={status.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {log.performed_by === 'user' ? `User ${valueOrDash(log.user_id)}` : 'System'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {valueOrDash(log.user_role || log.system_task)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {valueOrDash(log.record_id)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {valueOrDash(log.record_type)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={log.log_message || ''}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 360,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {log.log_message}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {valueOrDash(log.ip_address)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {valueOrDash(log.request_id)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={12} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {renderDetail(log)}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        justifyContent="space-between"
        sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Showing {numberFormat(pagination.from)}-{numberFormat(pagination.to)} of {numberFormat(pagination.total)}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={perPage}
              onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
            >
              {perPageOptions.map((option) => (
                <MenuItem key={option} value={Number(option)}>
                  {Number(option).toLocaleString()} / page
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FirstPage />}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(1)}
          >
            First
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<KeyboardArrowLeft />}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </Button>
          {pageNumbers.map((page) => (
            <Button
              key={page}
              size="small"
              variant={page === currentPage ? 'contained' : 'outlined'}
              sx={{ minWidth: 36 }}
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          ))}
          <Button
            size="small"
            variant="outlined"
            endIcon={<KeyboardArrowRight />}
            disabled={currentPage >= lastPage}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </Button>
          <Button
            size="small"
            variant="outlined"
            endIcon={<LastPage />}
            disabled={currentPage >= lastPage}
            onClick={() => onPageChange(lastPage)}
          >
            Last
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

const preSx = {
  m: 0,
  p: 1.25,
  maxHeight: 240,
  overflow: 'auto',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: '#fff',
  fontSize: '0.78rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
};

export default LogsTable;
